import {Stepper, StepButton, Step, Box} from '@mui/material'
import {useEffect, useState} from 'react'
import SelectFrequency from './components/SelectFrequency'
import SelectDate from './components/SelectDate'
import SelectSurvey from './components/SelectSurvey'
import WbOutlineButton from '../../components/common/WbOutlineButton'
import WbButton from '../../components/common/WbButton'
import SelectEmailTemplate from './components/SelectEmailTemplate'
import SelectParticipants from './components/SelectParticipants'
import CheckInCycleProjection from './components/CheckInCycleProjection'
import {DEFAULT_CHECKIN_EMAIL_TEMPLATE} from '../../utils/constants'
import WbAlert from '../../components/common/WbAlert'
import checkinApi from '../../services/api/checkin/checkinApi'
import {useNavigate, useParams} from 'react-router-dom'
import paths from '../../routes/paths'
import WbLoader from '../../components/common/WbLoader'
import {getApiHookParams, useIdentityAuth} from '../../auth'
import {
  defaultParticipantFilter,
  stepBlockingMessage,
  toCanonicalParticipantFilter,
} from './checkInWizardRules'

const steps = [
  'Set Frequency',
  'Set Date',
  'Select Survey',
  'Invites',
  'Participants',
]
const checkInQuestonConst = {
  question: 'How are you feeling today?',
  options: [
    {value: 'happy', label: 'Happy', numValue: 5, emoji: '1f60a'},
    {value: 'calm', label: 'Calm', numValue: 4, emoji: '1f642'},
    {value: 'meh', label: 'Meh', numValue: 3, emoji: '1f610'},
    {value: 'sad', label: 'Sad', numValue: 2, emoji: '1f622'},
    {value: 'angry', label: 'Angry', numValue: 1, emoji: '1f620'},
  ],
}

export default function CheckInEdit() {
  const {user, authenticated, authLoading} = useIdentityAuth()
  var navigate = useNavigate()
  const {id} = useParams()

  const [loading, setLoading] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [completed, setCompleted] = useState({})
  const [frequency, setFrequency] = useState(0)
  const [date, setDate] = useState(new Date())
  const [skipSchoolHolidays, setSkipSchoolHolidays] = useState(true)
  const [surveyIds, setSurveyIds] = useState([])
  const [checkInQuestion, setCheckInQuestion] = useState({
    ...checkInQuestonConst,
  })
  const [emailTemplate, setEmailTemplate] = useState(
    DEFAULT_CHECKIN_EMAIL_TEMPLATE
  )
  const [useEmail, setUseEmail] = useState(false)
  const [startHour, setStartHour] = useState(8)
  const [startMin, setStartMin] = useState(0)
  const apiParams = getApiHookParams()
  const [count, setCount] = useState(0)
  const [participantFilter, setParticipantFilter] = useState(
    defaultParticipantFilter()
  )
  useEffect(() => {
    if (id !== '0') {
      setLoading(true)
      checkinApi
        .getById(id)
        .then(response => {
          setFrequency(response.frequency.toString())
          setSurveyIds([...response.checkInSurveys.map(s => s.surveyId)])
          setDate(response?.startDate)
          setSkipSchoolHolidays(response.skipSchoolHolidays)
          setCheckInQuestion(JSON.parse(response.checkInQuestion))
          setUseEmail(response.sendEmail)
          setStartHour(response.startHour)
          setStartMin(response.startMin)
          setEmailTemplate(JSON.parse(response.emailTemplate))
          setParticipantFilter(
            toCanonicalParticipantFilter(JSON.parse(response.participantFilter))
          )
          setCount(response.pupilCount)
          setLoading(false)
        })
        .catch(error => {
          console.error('Failed to load check-in survey suite', error)
          setLoading(false)
          WbAlert({
            type: 'error',
            message: 'We could not load this check-in. Please try again.',
          })
        })
    }
  }, [])
  const totalSteps = () => {
    return steps.length
  }

  const completedSteps = () => {
    return Object.keys(completed).length
  }

  const isLastStep = () => {
    return activeStep === totalSteps() - 1
  }

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps()
  }
  const canGoToNextStep = () => {
    const blockingMessage = stepBlockingMessage(activeStep, {
      frequency,
      surveyIds,
      participantFilter,
    })
    if (blockingMessage === null) return true

    WbAlert({type: 'error', message: blockingMessage})
    return false
  }
  const publishCheckIn = () => {
    // The owner is whoever is signed in. This used to read a 'user' object that only the legacy
    // login and the SuperAdmin redirect wrote to browser storage, so an Identity sign-in had none
    // and Publish threw before the request was ever sent.
    const owner = user ?? {}

    var checkIn = {
      id: id,
      startDate: date,
      startMin,
      startHour,
      sendEmail: useEmail,
      pupilCount: count,
      ownerName: owner.name ?? '',
      ownerEmail: owner.email ?? '',
      emailTemplate: JSON.stringify(emailTemplate),
      participantFilter: JSON.stringify(
        toCanonicalParticipantFilter(participantFilter)
      ),
      frequency: parseInt(frequency),
      status: 1,
      checkInQuestion: JSON.stringify(checkInQuestion),
      skipSchoolHolidays: skipSchoolHolidays,
      matId: apiParams.isMat ? apiParams.groupId : 0,
      schoolId: apiParams.isMat === false ? apiParams.schoolId : 0,
      checkInSurveys: [
        ...surveyIds.map(survey => ({
          surveyId: survey.id,
          name: survey.name,
          CheckInId: 0,
          questionCount: survey?.surveyQuestions?.length,
        })),
      ],
    }
    checkinApi
      .post(checkIn)
      .then(res => {
        if (res) {
          WbAlert({
            title: 'Success',
            message: 'Check-In saved',
            type: 'success',
          })
          navigate(paths.checkinSurveys)
        } else {
          WbAlert({
            title: 'Error',
            message: 'Error creating check in',
            type: 'error',
          })
        }
      })
      .catch(error => {
        // Without this a failed request looked like the button doing nothing.
        console.error('Failed to publish check-in', error)
        WbAlert({
          title: 'Error',
          message: 'We could not publish this check-in. Please try again.',
          type: 'error',
        })
      })
  }

  const handleNext = () => {
    if (!canGoToNextStep()) return
    if (activeStep === steps.length - 1) {
      publishCheckIn()
      return
    }
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all steps have been completed,
          // find the first step that has been completed
          steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1
    setActiveStep(newActiveStep)
  }

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1)
  }

  const handleStep = step => () => {
    setActiveStep(step)
  }

  const handleComplete = () => {
    setCompleted({
      ...completed,
      [activeStep]: true,
    })
    handleNext()
  }

  const handleReset = () => {
    setActiveStep(0)
    setCompleted({})
  }

  const _renderStepContent = step => {
    switch (step) {
      case 0:
        return (
          <SelectFrequency frequency={frequency} setFrequency={setFrequency} />
        )
      case 1:
        return (
          <SelectDate
            date={date}
            setDate={setDate}
            skipSchoolHolidays={skipSchoolHolidays}
            setSkipSchoolHolidays={setSkipSchoolHolidays}
          />
        )
      case 2:
        return (
          <SelectSurvey
            surveyIds={surveyIds}
            setSurveyIds={setSurveyIds}
            checkInQuestion={checkInQuestion}
            setCheckInQuestion={setCheckInQuestion}
          />
        )
      case 3:
        return (
          <SelectEmailTemplate
            emailTemplate={emailTemplate}
            setEmailTemplate={setEmailTemplate}
            startHour={startHour}
            setStartHour={setStartHour}
            startMinute={startMin}
            setStartMinute={setStartMin}
            useEmail={useEmail}
            setUseEmail={setUseEmail}
            participantFilter={participantFilter}
          />
        )
      case 4:
        return (
          <>
            <SelectParticipants
              count={count}
              setCount={setCount}
              participantFilter={participantFilter}
              setParticipantFilter={setParticipantFilter}
            />
            <CheckInCycleProjection
              startDate={date}
              frequency={frequency}
              surveys={surveyIds}
            />
          </>
        )

      default:
        return <div>Not Found</div>
    }
  }
  return (
    <div className="white-container">
      {loading ? (
        <WbLoader />
      ) : (
        <>
          <Stepper alternativeLabel activeStep={activeStep}>
            {steps.map((label, index) => (
              <Step key={label} completed={completed[index]}>
                <StepButton color="inherit" onClick={handleStep(index)}>
                  {label}
                </StepButton>
              </Step>
            ))}
          </Stepper>
          {_renderStepContent(activeStep)}
          <Box sx={{display: 'flex', flexDirection: 'row', pt: 5}}>
            <WbOutlineButton
              disabled={activeStep === 0}
              onClick={handleBack}
              CustomOutlineButtonText={'Back'}
            />
            <Box sx={{flex: '1 1 auto'}} />
            <WbButton
              onClick={handleNext}
              CustomButtonText={
                activeStep === steps.length - 1 ? 'Publish' : 'Next'
              }
            />
          </Box>
        </>
      )}
    </div>
  )
}
